import { UnreadBadge } from './UnreadBadge';

interface ConversationSummary {
  id: string;
  type: string;
  dependentId?: string;
  title?: string;
  dependent?: { id: string; name: string };
  careHome?: { id: string; name: string };
  otherParticipant?: { id: string; name: string };
  lastMessage?: {
    content: string;
    senderName: string;
    createdAt: string;
  };
  unreadCount: number;
}

interface ConversationListProps {
  conversations: ConversationSummary[];
  activeId: string | null;
  onSelect: (id: string) => void;
  loading?: boolean;
}

export function ConversationList({ conversations, activeId, onSelect, loading }: ConversationListProps) {
  if (loading) {
    return (
      <div className="p-4 text-center text-gray-400 text-sm">
        Loading conversations...
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="p-4 text-center text-gray-400 text-sm">
        No conversations yet
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {conversations.map((convo) => {
        const isActive = convo.id === activeId;
        const title =
          convo.type === 'CARE_HOME'
            ? `${convo.careHome?.name || 'Care Home'} — General`
            : convo.type === 'FAMILY'
            ? 'Family chat'
            : convo.type === 'DIRECT'
            ? convo.otherParticipant?.name || 'Direct message'
            : convo.dependent?.name || convo.title || 'Conversation';

        const preview = convo.lastMessage
          ? `${convo.lastMessage.senderName}: ${convo.lastMessage.content}`
          : 'No messages yet';

        const time = convo.lastMessage
          ? formatTime(convo.lastMessage.createdAt)
          : '';

        return (
          <button
            key={convo.id}
            onClick={() => onSelect(convo.id)}
            className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
              isActive ? 'bg-blue-50 border-l-2 border-blue-600' : ''
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium truncate ${isActive ? 'text-blue-700' : 'text-gray-900'}`}>
                    {title}
                  </span>
                  {convo.type === 'CARE_HOME' && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full shrink-0">
                      Announcement
                    </span>
                  )}
                  {convo.type === 'DIRECT' && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-primary-50 text-primary-700 rounded-full shrink-0">
                      Direct
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate mt-0.5">
                  {preview}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                {time && <span className="text-[10px] text-gray-400">{time}</span>}
                <UnreadBadge count={convo.unreadCount} />
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
